import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { GraduationCap, Menu, X, ArrowRight, Calendar, Tag } from "lucide-react";
import { blogPosts } from '../data/blog-mock';
import { useAuth } from '../context/AuthContext';

const BlogListing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white font-sans text-brand-dark selection:bg-brand-blue selection:text-white flex flex-col">
      {/* Navigation - Adapted for inner pages */}
      <nav className="fixed w-full bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
                src="https://customer-assets.emergentagent.com/job_e9aa37b0-2814-4b9e-8c4c-03a6b3b48503/artifacts/hffkvssr_LogoAppadrino_color.png" 
                alt="Appadrino Logo" 
                className="h-12 w-auto" 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#como-funciona" className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Cómo funciona</Link>
            <Link to="/#beneficios" className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Beneficios</Link>
            <Link to="/#testimonios" className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Testimonios</Link>
            <Link to="/educacion" className="text-brand-blue font-bold transition-colors">Educación</Link>
            <div className="flex items-center gap-4 ml-4">
              {user ? (
                <Link to="/dashboard">
                    <Button className="bg-brand-blue text-white font-bold rounded-full px-6">
                        Ir al Dashboard
                    </Button>
                </Link>
              ) : (
                <>
                    <Link to="/login">
                        <Button variant="ghost" className="font-bold text-brand-blue hover:text-brand-blue/80 hover:bg-brand-lightBlue">
                        Ingresar
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold rounded-full px-6 shadow-lg shadow-brand-green/20">
                        Crear Cuenta
                        </Button>
                    </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-6 h-6 text-brand-dark" /> : <Menu className="w-6 h-6 text-brand-dark" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white border-b border-gray-100 p-4 flex flex-col gap-4 shadow-xl">
            <Link to="/" className="text-left p-2 text-gray-600 font-medium">Inicio</Link>
            <Link to="/educacion" className="text-left p-2 text-brand-blue font-bold">Educación</Link>
            <div className="flex flex-col gap-3 mt-2">
              {user ? (
                <Link to="/dashboard">
                    <Button className="w-full bg-brand-blue text-white font-bold">
                        Ir al Dashboard
                    </Button>
                </Link>
              ) : (
                <>
                    <Link to="/login">
                        <Button variant="outline" className="w-full border-brand-blue text-brand-blue font-bold">
                        Ingresar
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button className="w-full bg-brand-green text-brand-dark font-bold">
                        Crear Cuenta
                        </Button>
                    </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-brand-lightBlue/30">
        <div className="container mx-auto px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1 mb-4 text-sm font-bold tracking-wide text-brand-blue uppercase bg-white rounded-full shadow-sm">
                Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-6">
                Blog Appadrino
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Un espacio dedicado a la educación financiera para familias. Aprende a construir un futuro sólido para tus hijos, paso a paso.
            </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-white flex-grow">
        <div className="container mx-auto px-4 md:px-6">
            <div className="grid md:grid-cols-3 gap-8">
                {blogPosts.map((post) => (
                    <Card key={post.id} className="group border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col bg-white h-full">
                        <div className="h-48 overflow-hidden relative">
                            <img 
                                src={post.image} 
                                alt={post.title} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-brand-blue uppercase tracking-wider">
                                {post.category}
                            </div>
                        </div>
                        <CardHeader className="pb-2">
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                <Calendar className="w-3 h-3" />
                                {post.date}
                            </div>
                            <h3 className="text-xl font-bold text-brand-dark group-hover:text-brand-blue transition-colors leading-tight">
                                {post.title}
                            </h3>
                        </CardHeader>
                        <CardContent className="flex-grow">
                            <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                                {post.excerpt}
                            </p>
                        </CardContent>
                        <CardFooter className="pt-0 pb-6">
                            <Link 
                                to={`/educacion/${post.slug}`} 
                                className="flex items-center gap-2 text-brand-blue font-bold text-sm hover:translate-x-1 transition-transform"
                            >
                                Leer más <ArrowRight className="w-4 h-4" />
                            </Link>
                        </CardFooter>
                    </Card>
                ))}
            </div>
        </div>
      </section>

      {/* Simplified Footer */}
      <footer className="bg-gray-50 py-12 border-t border-gray-200 mt-auto">
        <div className="container mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
                <GraduationCap className="w-6 h-6 text-brand-blue" />
                <span className="text-xl font-bold text-brand-blue">Appadrino</span>
            </div>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-8">
                Plataforma de ahorro colaborativo para la educación superior. Construyendo futuros brillantes, juntos.
            </p>
            <div className="text-sm text-gray-400">
                © 2025 Appadrino. Todos los derechos reservados.
            </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogListing;
